import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Button } from "react-bootstrap";
import { GetDynamicFormsForTaskPage } from "../../../../../services/api/task-api";
import PropTypes from "prop-types";
import { LoginContext } from "../../../../../services/api/authentication-api";
import DownloadFormModal from "../../task-form/download-form-modal";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { Skeleton } from "@progress/kendo-react-indicators";

const TaskFormTable = ({ forms, setForms, taskId, values, dynamicFormStatus, labels, disabled }) => {
  const user = useContext(LoginContext);
  const history = useHistory();
  const [formId, setFormId] = useState(null);
  const [loading, setLoading] = useState(false);
  const openForm = (ev, id) => {
    ev.preventDefault();
    history.push("/tasks/" + taskId + "/form/" + id);
  };

  const GetTaskForms = async (id) => {
    setLoading(true);
    try {
      let result = await GetDynamicFormsForTaskPage(id, taskId);
      if (result.status === 200) {
        setForms(result.data);
      } else {
        setForms([]); // fallback
      }
    } catch (e) {
      console.error("Failed to fetch forms", e);
      setForms([]); // fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (values.workItemType) {
      GetTaskForms(values.workItemType);
    }
  }, [values.workItemType]);

  return (
    <React.Fragment>
      <h4 className="heading">{labels?.TaskSingular ?? 'Task'} {labels?.FormPlural ?? 'Forms'}</h4>

      <div className="task-form-table">
        <table>
          <thead>
            <th className="pl-2 w-75">{labels?.FormSingular ?? 'Form'}</th>
            {dynamicFormStatus && (
              <th className="text-center pr-2 w-25">Status</th>
            )}
            <th className="text-center pr-2 w-25 form-actions">Actions</th>
          </thead>
          {loading ? (
            <tbody>
              {Array.from({ length: 8 }).map((_, idx) => (
                <tr key={idx}>
                  <td className="skeleton-cell p-2 pr-4 w-75" style={{ border: "none" }}>
                    <Skeleton
                      shape="text"
                      className="skeleton-text"
                      animation={{ type: "pulse" }}
                    />
                  </td>
                  {dynamicFormStatus && (
                    <td className="skeleton-cell text-center pr-4 w-25" style={{ border: "none" }}>
                      <Skeleton
                        shape="text"
                        className="skeleton-text"
                        animation={{ type: "pulse" }}
                      />
                    </td>
                  )}
                  <td className="skeleton-cell pr-2 w-25 text-center form-actions" style={{ border: "none" }}>
                    <Skeleton
                      shape="text"
                      className="skeleton-text"
                      animation={{ type: "pulse" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              {forms?.length === 0 && (
                <tr>
                  <td colSpan={dynamicFormStatus ? 3 : 2} className="text-center">No {labels?.FormPlural ?? 'forms'} available</td>
                </tr>
              )}
              {forms.map((form, index) => {
                return (
                  <tr key={form?.Id}>
                    <td className="pl-2 w-75">
                      <span>{form.Label}</span>
                    </td>
                    {dynamicFormStatus && (
                      <td className="text-center pr-2 w-25">
                        {form.StatusName ? (
                          <>
                            <FluentBadge
                              shape="rounded"
                              appearance="tint"
                              size="extra-large"
                              text={form.StatusName}
                              style={{
                                backgroundColor: form.StatusColorBackground,
                                color: form.StatusColorFont,
                                borderStyle: "none",
                                borderWidth: "0.5px",
                                borderColor: form.StatusColorBorder
                              }}
                            />
                          </>
                        ) : (<>
                        {/* Frosted Grey (Light) => color requested by Product team*/}
                          <FluentBadge
                            shape="rounded"
                            appearance="tint"
                            size="extra-large"
                            text="Locked"
                            style={{
                              backgroundColor: "#CDD6D8",
                              color: "#3B4447",
                              borderColor: "#69797E",
                              borderStyle: "none",
                              borderWidth: "0.5px"
                            }}
                          />
                        </>)}
                      </td>
                    )}
                    <td className="pr-2 w-25 text-center form-actions">
                      <Button variant="link" disabled>
                        <FluentUiIcon
                          name="LinkRegular"
                          size={20}
                          color="#BDBDBD"
                        />
                      </Button>
                      <Button
                        variant="link"
                        onClick={(ev) => openForm(ev, form.Id)}
                        disabled={form?.IsFormDisable || disabled}
                      >
                        <FluentUiIcon
                          name="EditRegular"
                          size={20}
                          color={!(form?.IsFormDisable || disabled) ? "#0F6CBD" : "#BDBDBD"}
                        />
                      </Button>
                      <Button
                        variant="link"
                        onClick={() =>
                          setFormId({ Id: form.Id, Name: form.Label })
                        }
                        disabled={form?.IsDownloadDisable}
                      >
                        <FluentUiIcon
                          name="ArrowDownloadRegular"
                          size={20}
                          color="#0F6CBD"
                        />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )
          }

        </table>
      </div>
      {formId !== null && (
        <DownloadFormModal
          formId={formId}
          setFormId={setFormId}
          taskId={taskId}
          valueName={values.Name}
          userName={user?.currentUser?.profile?.userNameFull}
        />
      )}
    </React.Fragment>
  );
};

TaskFormTable.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.string.isRequired,
      Label: PropTypes.string.isRequired,
    })
  ).isRequired,
  setForms: PropTypes.func.isRequired,
  taskId: PropTypes.number.isRequired,
  values: PropTypes.shape({
    workItemType: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }).isRequired,
  labels: PropTypes.shape({
      ProjectSingular: PropTypes.string,
      ProjectPlural: PropTypes.string,
      ProjectSingularPossessive: PropTypes.string,
      ProjectPluralPossessive: PropTypes.string,
      TaskSingular: PropTypes.string,
      TaskPlural: PropTypes.string,
      TaskSingularPossessive: PropTypes.string,
      TaskPluralPossessive: PropTypes.string,
      FormSingular: PropTypes.string,
      FormPlural: PropTypes.string,
      FormSingularPossessive: PropTypes.string,
      FormPluralPossessive: PropTypes.string,
  }),
  disabled: PropTypes.bool,
};

export default TaskFormTable;
